import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { HeaderComponent } from '../../../shared/header/header.component';
import { CashRegisterService } from '../../../../services/cash-register/cash-register.service';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';
import { CashRegisterAnalysis, UserProfileResponse } from '../../../../interfaces/cash-register';
import { BrandService } from '../../../../services/brand/brand.service';

type StatusFiltro = 'TODOS' | 'ABERTOS_OK' | 'PRECISA_SANGRIA' | 'FECHADO_COM_SOBRA' | 'FECHADO_OK';

const LIMITE_SANGRIA = 1000;
const LIMITE_FECHAMENTO = 200;

@Component({
  selector: 'app-analise-caixas',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent, RouterLink],
  templateUrl: './analise-caixas.component.html',
})
export class AnaliseCaixasComponent implements OnInit {
  caixas: CashRegisterAnalysis[] = [];
  filtroUnidade = '';
  filtroStatus: StatusFiltro = 'TODOS';
  filtroBusca = '';
  isLoading = false;
  erro: string | null = null;
  private username = '';

  constructor(
    private router: Router,
    private cashRegisterService: CashRegisterService,
    private localStorage: LocalStorageService,
    private brandService: BrandService,
  ) {}

  async ngOnInit() {
    this.username = this.localStorage.get('login') as string;
    if (!this.username) {
      this.router.navigate(['/home']);
      return;
    }

    const profileLocal = this.localStorage.get('profile') as string | null;
    if (profileLocal !== 'Administrador') {
      const perfil: UserProfileResponse | null = await this.cashRegisterService.getUserProfile(this.username);
      if (!perfil?.isSupervisorGlobal && !perfil?.isSupervisorUnit) {
        this.router.navigate(['/lancamentos-financeiros']);
        return;
      }
    }

    await this.carregar();
  }

  async carregar() {
    this.isLoading = true;
    this.erro = null;
    try {
      this.caixas = await this.cashRegisterService.getAnalysisSummary(this.username, undefined, this.brandService.cdEmpresa);
    } catch (e: any) {
      this.erro = e?.error?.feedback || 'Erro ao carregar análise.';
    } finally {
      this.isLoading = false;
    }
  }

  status(c: CashRegisterAnalysis): StatusFiltro {
    if (c.isOpen && c.currentBalance > LIMITE_SANGRIA) return 'PRECISA_SANGRIA';
    if (c.isOpen) return 'ABERTOS_OK';
    if (!c.isOpen && c.currentBalance > LIMITE_FECHAMENTO) return 'FECHADO_COM_SOBRA';
    return 'FECHADO_OK';
  }

  statusLabel(s: StatusFiltro): string {
    switch (s) {
      case 'ABERTOS_OK': return 'Aberto';
      case 'PRECISA_SANGRIA': return 'Precisa sangrar';
      case 'FECHADO_COM_SOBRA': return 'Fechado com sobra';
      case 'FECHADO_OK': return 'Fechado';
      default: return '—';
    }
  }

  statusBg(s: StatusFiltro): string {
    switch (s) {
      case 'ABERTOS_OK': return '#dcfce7';
      case 'PRECISA_SANGRIA': return '#ffedd5';
      case 'FECHADO_COM_SOBRA': return '#fee2e2';
      case 'FECHADO_OK': return '#e5e7eb';
      default: return '#f3f4f6';
    }
  }

  statusFg(s: StatusFiltro): string {
    switch (s) {
      case 'ABERTOS_OK': return '#166534';
      case 'PRECISA_SANGRIA': return '#9a3412';
      case 'FECHADO_COM_SOBRA': return '#991b1b';
      case 'FECHADO_OK': return '#374151';
      default: return '#626272';
    }
  }

  get unidadesDistintas(): { id: number; name: string }[] {
    const map = new Map<number, string>();
    for (const c of this.caixas) if (!map.has(c.establishmentId)) map.set(c.establishmentId, c.establishmentName);
    return Array.from(map.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }

  get caixasFiltrados(): CashRegisterAnalysis[] {
    return this.caixas.filter(c => {
      if (this.filtroUnidade && String(c.establishmentId) !== this.filtroUnidade) return false;
      if (this.filtroStatus !== 'TODOS' && this.status(c) !== this.filtroStatus) return false;
      if (this.filtroBusca) {
        const t = this.filtroBusca.toLowerCase();
        const alvo = `${c.name} ${c.username ?? ''}`.toLowerCase();
        if (!alvo.includes(t)) return false;
      }
      return true;
    });
  }

  get totais() {
    const src = this.caixasFiltrados;
    return {
      total: src.length,
      abertos: src.filter(c => c.isOpen).length,
      precisaSangria: src.filter(c => this.status(c) === 'PRECISA_SANGRIA').length,
      fechadoSobra: src.filter(c => this.status(c) === 'FECHADO_COM_SOBRA').length,
      saldoInicialTotal: src.reduce((acc, c) => acc + (c.initialBalance || 0), 0),
      saldoTotal: src.reduce((acc, c) => acc + (c.currentBalance || 0), 0),
      entradasTotal: src.reduce((acc, c) => acc + (c.ingressBalance || 0), 0),
      saidasTotal: src.reduce((acc, c) => acc + (c.egressBalance || 0), 0),
      canceladosTotal: src.reduce((acc, c) => acc + (c.cancelledBalance || 0), 0),
    };
  }

  formatCurrency(v: number | null | undefined): string {
    const val = v ?? 0;
    return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  formatDate(v: string | null): string {
    if (!v) return '—';
    const d = new Date(v);
    return d.toLocaleDateString('pt-BR');
  }

  voltar() {
    this.router.navigate(['/lancamentos-financeiros']);
  }
}
